import {useCallback, useEffect, useMemo, useState} from 'react';
import {QueryFunctionContext, useQuery} from 'react-query';
import {format, parseISO} from 'date-fns';
import {capitalize} from 'lodash';

import ViewDetails from 'components/ViewDetails';
import StatusBadge from 'components/StatusBadge';

import {DataItem, Driver, ListResponse, Route, SelectOption} from 'types';
import api from 'api';
import {DATETIME_FORMAT} from 'constants/datetime';
import useSort from 'hooks/useSort';
import {mapStatusColor} from 'constants/mappings';
import useDebounce from 'hooks/useDebounce';
import {useHistory, useRouteMatch} from 'react-router-dom';

export const ALL_STATUSES = [
  'new',
  'processing',
  'processed',
  'awaiting',
  'active',
  'completed',
].join(',');

export const ACTIVE_STATUSES = [
  'new',
  'processing',
  'processed',
  'awaiting',
  'active',
].join(',');

export const COMPLETED_STATUSES = 'completed';

async function getRoutes({queryKey}: QueryFunctionContext<any[]>) {
  const [, page, order, status, keyWord, pageSize, filter] = queryKey;
  const {data} = await api.get<ListResponse<Route>>('/routes/', {
    params: {
      order: order.includes('number') ? '' : order,
      status: status ? status : undefined,
      search: keyWord ? keyWord : undefined,
      offset: +page * +pageSize,
      limit: +pageSize,
      created_at_after: filter.dateCreated?.startDate
        ? format(filter.dateCreated?.startDate, 'yyyy-MM-dd')
        : undefined,
      created_at_before: filter.dateCreated?.endDate
        ? format(filter.dateCreated?.endDate, 'yyyy-MM-dd')
        : undefined,
      driver_id: filter.driver?.value,
    },
  });

  return data;
}

async function getDrivers({queryKey}: QueryFunctionContext<any[]>) {
  const [,] = queryKey;
  const {data} = await api.get<ListResponse<Driver>>('/drivers/', {
    params: {
      limit: 100,
    },
  });

  return data;
}

type FilterType = {
  driver: SelectOption | undefined;
  dateCreated: {
    startDate: Date | null;
    endDate: Date | null;
  };
};

function useRoutes() {
  const {params} = useRouteMatch<{page: string | undefined}>();
  const [currentPage, setCurrentPage] = useState(parseInt(params.page || '1'));
  const history = useHistory();
  const [keyWord, setKeyWord] = useState(
    sessionStorage.getItem(`search_history`) || ''
  );
  const [activeTab, setActiveTab] = useState(
    sessionStorage.getItem(`history_tab`) || ''
  );
  const [pageSize, setPageSize] = useState(40);
  const [filter, setFilter] = useState<FilterType>({
    dateCreated: {
      startDate: sessionStorage.getItem('route_date_created_start')
        ? new Date(
            JSON.parse(sessionStorage.getItem('route_date_created_start') || '')
          )
        : null,
      endDate: sessionStorage.getItem('route_date_created_end')
        ? new Date(
            JSON.parse(sessionStorage.getItem('route_date_created_end') || '')
          )
        : null,
    },
    driver: undefined,
  });

  const {sortBy, sortOrder, order, onSort} = useSort('created_at');
  const [status, setStatus] = useState('');
  const searchDebounced = useDebounce(keyWord, 1000);

  const {data: routes, isLoading} = useQuery(
    [
      'routes',
      Number(currentPage) - 1,
      order,
      activeTab,
      searchDebounced,
      String(pageSize),
      filter,
    ],
    getRoutes
  );
  const {data: drivers} = useQuery(['get_drivers'], getDrivers);

  useEffect(() => {
    if (params.page) {
      setCurrentPage(parseInt(params.page));
    }
  }, [params.page]);

  useEffect(() => {
    sessionStorage.setItem(
      'route_date_created_start',
      filter.dateCreated?.startDate
        ? JSON.stringify(filter.dateCreated?.startDate)
        : ''
    );
    sessionStorage.setItem(
      'route_date_created_end',
      filter.dateCreated?.endDate
        ? JSON.stringify(filter.dateCreated?.endDate)
        : ''
    );
  }, [filter.dateCreated?.startDate, filter.dateCreated?.endDate]);

  const data = useMemo(
    function () {
      return routes
        ? routes.results.map((r) => ({
            ...r,
            number: `#${r.id}`,
            status: (
              <StatusBadge color={mapStatusColor[r.status]}>
                {capitalize(
                  r.status === 'awaiting'
                    ? 'delivering'
                    : r.status === 'processed'
                    ? 'calculated'
                    : r.status
                )}
              </StatusBadge>
            ),            
            created_at: format(parseISO(r.created_at), DATETIME_FORMAT),
            route_deliveries_count: `${r.completed_deliveries_count}/${r.total_deliveries_count}`,
            view: <ViewDetails disabled={true} to={`/history/routes/:id`} />,
          }))
        : [];
    },
    [routes]
  );

  const handleViewDetails = useCallback(
    (item: DataItem) => {
      history.push(`/history/routes/${item.id}`);
    },
    [history]
  );

  const onchangeTab = useCallback(
    (tab: string) => {
      setActiveTab(tab);
      setCurrentPage(1);
      sessionStorage.setItem(`history_tab`, tab);
      history.push(`/history/routes/page/1`);
    },
    [setActiveTab, history]
  );

  return {
    data,
    isLoading,
    activeTab,
    status,
    sortBy,
    sortOrder,
    onchangeTab,
    onSort,
    setStatus,
    keyWord,
    setKeyWord,
    totalPages:
      routes && routes?.limit ? Math.ceil(routes.count / routes.limit) : 0,
    totalDeliveries: routes?.total_deliveries_count,
    count: routes?.count || 0,
    pageSize,
    setPageSize,
    filter,
    setFilter,
    history,
    drivers: drivers?.results || [],
    currentPage,
    setCurrentPage,
    handleViewDetails,
  };
}

export default useRoutes;
